import { useEffect, useMemo, useState } from "react";

const RELEASES_URL = "https://github.com/tiny-cloud-ventures/poly-grid/releases";

const PLATFORM_LABELS = {
  "mac-arm64": "Apple Silicon",
  "mac-x64": "Intel Mac",
  linux: "Linux",
  windows: "Windows",
};

const FALLBACK_PLATFORM = "mac-arm64";

let configPromise = null;
function loadConfig() {
  if (typeof fetch === "undefined") return Promise.resolve(null);
  if (!configPromise) {
    configPromise = fetch("/download-config.json")
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return configPromise;
}

export function detectPlatform() {
  if (typeof navigator === "undefined") return FALLBACK_PLATFORM;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) return detectMacArch();
  if (/Linux/i.test(platform) && !/Android/i.test(ua)) return "linux";
  if (/Win/i.test(platform)) return "windows";
  return FALLBACK_PLATFORM;
}

function detectMacArch() {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "mac-arm64";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return "mac-arm64";
    const renderer = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "");
    // Apple Silicon GPUs report "Apple GPU" / "Apple M…". Intel iGPUs show "Intel".
    if (/Intel/i.test(renderer)) return "mac-x64";
    return "mac-arm64";
  } catch {
    return "mac-arm64";
  }
}

function pickOrder(detected) {
  switch (detected) {
    case "mac-x64":
      return ["mac-x64", "mac-arm64", "linux"];
    case "linux":
      return ["linux", "mac-arm64", "mac-x64"];
    default:
      return ["mac-arm64", "mac-x64", "linux"];
  }
}

function resolveDownload(config, platform) {
  const releasesFallback = {
    primary: {
      url: RELEASES_URL,
      label: "Download",
      platform: "unknown",
      external: true,
    },
    others: [],
    available: false,
    config,
    platform,
  };

  if (!config?.platforms) return releasesFallback;

  let primary = null;
  const others = [];
  for (const key of pickOrder(platform)) {
    const a = config.platforms[key];
    if (!a) continue;
    const entry = {
      url: a.url,
      label: PLATFORM_LABELS[key],
      platform: key,
      filename: a.filename,
      external: false,
    };
    if (!primary) primary = entry;
    else others.push(entry);
  }

  if (!primary) return releasesFallback;
  return { primary, others, available: true, config, platform };
}

export function useDownload() {
  const platform = useMemo(() => detectPlatform(), []);
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadConfig().then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return useMemo(() => resolveDownload(config, platform), [config, platform]);
}
